/**
 * NX-2801 — the metric-explanations preference: VISIBLE BY DEFAULT, hideable with a toggle.
 *
 * Papa's direction (2026-08-26, superseding NX-2798's hover-only ⓘ): a client should not have to
 * know to hover; the explanation text renders on the tile unless the viewer turns it off. The
 * choice persists per browser via localStorage and FAILS OPEN — any storage error (private window,
 * blocked site data) means explanations show, because the safe failure is "too much help".
 *
 * A module-level external store (useSyncExternalStore) rather than a context provider: every
 * screen and isolated test gets the default with zero wrapping, and the toggle needs no plumbing.
 */
import { useSyncExternalStore } from "react";

const STORAGE_KEY = "senesight.explanations";

function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "hidden";
  } catch {
    return true; // fail-open: storage unavailable => explanations visible
  }
}

let visible = readStored();
const listeners = new Set<() => void>();

export function toggleExplanations(): void {
  visible = !visible;
  try {
    window.localStorage.setItem(STORAGE_KEY, visible ? "visible" : "hidden");
  } catch {
    // preference simply won't persist — the in-session value still applies
  }
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Server has no localStorage to read — fail open, same as a client storage error. */
function getServerSnapshot(): boolean {
  return true;
}

/** Reactive read — re-renders subscribers when the toggle flips. */
export function useExplanationsVisible(): boolean {
  return useSyncExternalStore(subscribe, () => visible, getServerSnapshot);
}

/** Test seam: reset to the stored (or default) state between tests. */
export function resetExplanationsForTest(): void {
  visible = readStored();
  for (const l of listeners) l();
}
